import { computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, map, of, pipe, switchMap, tap } from 'rxjs';
import { HashMap, LocalizationService } from '@capital-access/common/localization';
import { AssetType, SecuritiesRepository } from '@capital-access/common/security';
import { LoadStatus } from '@capital-access/common/utils';
import { COMMON_COMPONENTS_LOCALIZATION_SCOPE } from '../../common-components.const';
import { SaveFilterCriteria } from '../saved-filters';
import { SecuritySuggesterItem, SecuritySuggesterItemType } from './models/security-suggester-item.model';

interface SecuritySuggesterState {
  loadStatus: LoadStatus;
  maxSelectionLimit: number;
  defaultSecurity: SecuritySuggesterItem | null;
  securityLists: SecuritySuggesterItem[];
  suggestions: SecuritySuggesterItem[];
  selectedItems: SecuritySuggesterItem[];
  criteriaApplied: boolean;
}

const initialState: SecuritySuggesterState = {
  loadStatus: LoadStatus.Pristine,
  maxSelectionLimit: 0,
  defaultSecurity: null,
  securityLists: [],
  suggestions: [],
  selectedItems: [],
  criteriaApplied: false
};

export const SecurityMultiSuggesterStore = signalStore(
  withState<SecuritySuggesterState>(initialState),
  withComputed(store => ({
    selectedSecuritiesCount: computed(() =>
      store.selectedItems().reduce((count, item) => {
        return item.type === SecuritySuggesterItemType.Individual ? count + 1 : count + (item.itemsCount || 0);
      }, 0)
    )
  })),
  withMethods(store => {
    const securitiesRepository = inject(SecuritiesRepository);
    const localization = toSignal(inject(LocalizationService).getLocalization(COMMON_COMPONENTS_LOCALIZATION_SCOPE), {
      initialValue: {} as HashMap
    });

    const setSelectedItemsFromCriteria = (criteria: SaveFilterCriteria) => {
      const details = criteria.details as {
        securityListIds?: number[];
        singleSecurityIds?: number[];
      };

      const listItems = details.securityListIds?.length
        ? store.securityLists().filter(l => details.securityListIds!.includes(l.id))
        : [];

      const individualIds = details.singleSecurityIds ?? [];
      if (!individualIds.length) {
        patchState(store, {
          selectedItems: listItems,
          criteriaApplied: true
        });
        return;
      }

      securitiesRepository.getByIds(individualIds).subscribe(securities => {
        const individualItems: SecuritySuggesterItem[] = securities.map(s => ({
          id: s.id!,
          name: s.name,
          ticker: s.ticker ?? '',
          stockExchange: s.stockExchange ?? '',
          type: SecuritySuggesterItemType.Individual,
          category: localization()['securities'],
          disabled: false
        }));
        patchState(store, {
          selectedItems: [...listItems, ...individualItems],
          criteriaApplied: true
        });
      });
    };

    return {
      setInitialData: (
        securityLists: SecuritySuggesterItem[],
        maxSelectionLimit: number,
        filterCriteriaToBeEdited: SaveFilterCriteria | undefined
      ) => {
        patchState(store, {
          securityLists,
          suggestions: securityLists,
          maxSelectionLimit
        });
        if (filterCriteriaToBeEdited) {
          setSelectedItemsFromCriteria(filterCriteriaToBeEdited);
        }
      },

      onSelectionChange(selectedItems: SecuritySuggesterItem[]) {
        patchState(store, { selectedItems });
        patchState(store, {
          suggestions: [
            ...disableExceededItems(
              store.suggestions(),
              selectedItems,
              store.selectedSecuritiesCount(),
              store.maxSelectionLimit()
            )
          ]
        });
      },

      onSearchTextChange: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { loadStatus: LoadStatus.Loading })),
          switchMap(searchText => {
            if (searchText.trim().length > 2) {
              return securitiesRepository
                .searchSecurities({ searchText, take: 20, skip: 0, assetTypes: [AssetType.Equity] })
                .pipe(
                  map(securities => {
                    const securitySuggestions: SecuritySuggesterItem[] = securities
                      .filter(item => !!item.ticker)
                      .map(item => ({
                        id: item.id,
                        name: item.name,
                        type: SecuritySuggesterItemType.Individual,
                        category: localization()['securities'],
                        ticker: item.ticker,
                        stockExchange: item.stockExchange,
                        disabled:
                          store.selectedSecuritiesCount() === store.maxSelectionLimit() &&
                          !store.selectedItems().some(selected => selected.id === item.id)
                      }));
                    const listSuggestions = disableExceededItems(
                      store
                        .securityLists()
                        .filter(
                          item =>
                            item.name.toLowerCase().includes(searchText.toLowerCase()) ||
                            item.ticker?.toLowerCase().includes(searchText.toLowerCase())
                        ),
                      store.selectedItems(),
                      store.selectedSecuritiesCount(),
                      store.maxSelectionLimit()
                    );

                    patchState(store, {
                      suggestions: [...listSuggestions, ...securitySuggestions],
                      loadStatus: LoadStatus.Loaded
                    });
                    return store.suggestions();
                  }),
                  catchError(error => {
                    const filteredLists = searchText.trim()
                      ? store
                          .securityLists()
                          .filter(
                            item =>
                              item.name.toLowerCase().includes(searchText.toLowerCase()) ||
                              item.ticker?.toLowerCase().includes(searchText.toLowerCase())
                          )
                      : store.securityLists();

                    patchState(store, {
                      suggestions: [
                        ...disableExceededItems(
                          filteredLists,
                          store.selectedItems(),
                          store.selectedSecuritiesCount(),
                          store.maxSelectionLimit()
                        )
                      ],
                      loadStatus: LoadStatus.Failed
                    });
                    return of(store.suggestions());
                  })
                );
            } else {
              patchState(store, {
                suggestions: [
                  ...disableExceededItems(
                    store.securityLists(),
                    store.selectedItems(),
                    store.selectedSecuritiesCount(),
                    store.maxSelectionLimit()
                  )
                ],
                loadStatus: LoadStatus.Loaded
              });
              return of(store.suggestions());
            }
          })
        )
      )
    };
  })
);

function disableExceededItems(
  suggestions: SecuritySuggesterItem[],
  selectedItems: SecuritySuggesterItem[],
  selectedCount: number,
  maxSelectionLimit: number
): SecuritySuggesterItem[] {
  return suggestions.map(item => {
    if (item.type === SecuritySuggesterItemType.List) {
      return {
        ...item,
        disabled:
          item.itemsCount + selectedCount > maxSelectionLimit &&
          !selectedItems.some(selected => selected.id === item.id)
      };
    } else {
      return {
        ...item,
        disabled: selectedCount === maxSelectionLimit && !selectedItems.some(selected => selected.id === item.id)
      };
    }
  });
}
